import { hydrateTemperatureTrends } from './temperature-trends.js';
import {appendCameraToUrl} from '../lib/camera-state.js';
import { hydrateThermalContext } from './station-thermal.js';
// Scheda stazione v2 — port 1:1 di getPopupText e delle sue dipendenze da
// assets/js/map-markers.js (v1 resta la fonte di verità per la parità dei
// VALORI: stessi guard metricAvailable, stessi decimali, stesse etichette).
//
// Globali browser richiesti (caricati in v2/index.php PRIMA del bundle):
// - window.chroma           (vendor v1, per hero color e colori grafico)
// - window.WSPalettes       (via lib/scales.js)
// - window.WSWeatherApi     (client API v1: getStationArchive)
//
// Adattamenti dichiarati rispetto alla v1 (nessuna modifica alla semantica dei valori):
// - `parametro` arriva come argomento (v1 lo leggeva da #selectParametro);
// - i selettori sono scoped su `container` (v1 usava document + id CSS-escaped);
// - Chart.js è iniettato con path ASSOLUTO /assets/vendor/chart/... (la pagina
//   sta in /v2/, il relativo v1 sbaglierebbe path) — stesso pattern wsEnsureChart;
// - il confronto identità v1 `color === rainIntervalColor` nel grafico è
//   sostituito dal check sul tipo di ritorno della scala (stringa = scala a
//   intervalli, oggetto chroma = .hex()): in v1 quel ramo era comunque morto
//   perché getColorScale non ritorna mai rainIntervalColor;
// - tick/griglia del grafico leggono i token --ws-fg-soft/--ws-line (tema
//   scuro leggibile) con fallback esatto sulle costanti v1 (#627383 / rgba(16,32,45,0.07));
// - l'istanza Chart è agganciata al canvas (canvas._wsStationChart) invece che
//   a una Map globale per id: la scheda può essere ricreata più volte.

import {
    computeDerivedValue,
    stationNetworkFullLabel,
    stationNetworkBadgeLabel,
    finiteValue,
    chartUnitForParam,
    snowIsPlausible
} from '../lib/params.js';
import { getColorScale } from '../lib/scales.js';

// ======== Costanti (map-markers.js) ========
export const STATION_CHART_PARAMS = [
    { id: 'temperatura', label: 'Temp' },
    { id: 'rain', label: 'Pioggia' },
    { id: 'vento', label: 'Vento' },
    { id: 'pressione', label: 'Pressione' }
];

const WEBCAM_BASE_URL = 'https://cam.weathersicily.it';
const CHART_VENDOR_URL = '/assets/vendor/chart/chart.umd.min.js?v=4.4.9';

function stationAdConfig() {
    const config = window.WS_MAPS_ADS_CONFIG || {};
    if (!config.enabled || !config.client || !config.stationSlot) return null;
    return config;
}

function renderStationAd() {
    const config = stationAdConfig();
    if (!config) return '';
    return `
      <aside class="ws-station-card__ad" aria-label="Pubblicità" data-ws-maps-station-ad>
        <span class="ws-station-card__ad-label">Pubblicità</span>
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="${escapeHtml(config.client)}"
             data-ad-slot="${escapeHtml(config.stationSlot)}"></ins>
      </aside>
    `;
}

function initStationAd(container) {
    const wrapper = container.querySelector('[data-ws-maps-station-ad]');
    const ad = wrapper?.querySelector('.adsbygoogle');
    if (!wrapper || !ad || !stationAdConfig()) return;

    const requestAd = () => {
        if (ad.dataset.wsAdInitialized === 'true') return;
        ad.dataset.wsAdInitialized = 'true';
        try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch (_error) {}
    };

    if (!('IntersectionObserver' in window)) {
        requestAd();
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        requestAd();
    }, { rootMargin: '100px 0px', threshold: 0.05 });
    observer.observe(wrapper);
}

// ======== Helper base (map-markers.js:356-445) ========
function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, ch => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    }[ch]));
}

function sanitizeHttpUrl(value) {
    if (!value) return '';
    try {
        const url = new URL(String(value), window.location.href);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (e) {
        return '';
    }
}

function isWeatherSicilyStation(station, networkLabel) {
    const officialFlag = Number(station.officialFlag ?? station.official_flag ?? 0);
    const network = String(
        station.network_category ?? station.stationType ?? station.network ??
        station.type ?? station.tipo ?? station.rete ?? ''
    ).trim().toUpperCase();
    return officialFlag === 1
        || ['WS', 'CUSTOM', 'WEATHER SICILY'].includes(network)
        || String(networkLabel ?? '').trim().toLowerCase() === 'weather sicily';
}

function stationNetworkLogo(station, networkLabel) {
    if (String(station.network_category || '').toLowerCase() === 'cnr') {
        return { src: '/assets/images/networks/cnr.svg', alt: 'CNR – Consiglio Nazionale delle Ricerche', href: 'https://www.cnr.it', modifier: 'cnr' };
    }
    if (isWeatherSicilyStation(station, networkLabel)) {
        return { src: '/logo.png', alt: 'Weather Sicily', modifier: 'weather-sicily' };
    }

    const network = String(
        station.network_category ?? station.stationType ?? station.network ??
        station.type ?? station.tipo ?? station.rete ?? ''
    ).trim().toLowerCase();
    if (network === 'meteolicata') {
        return {
            src: '/assets/images/networks/meteolicata.webp',
            alt: 'MeteoLicata.it',
            href: 'https://www.meteolicata.it',
            modifier: 'meteolicata'
        };
    }
    return null;
}

function formatMetricValue(value, decimals = 1) {
    const n = finiteValue(value);
    if (n === null) return '';
    return Number.isInteger(n) ? String(n) : n.toFixed(Math.max(0, decimals));
}

function cleanWindDirection(value) {
    const raw = String(value ?? '').trim().toUpperCase();
    if (!raw || raw === '-' || raw === '-1' || raw === 'NULL' || raw === 'NAN') return '';

    const validCompass = new Set([
        'N','NNE','NE','ENE','E','ESE','SE','SSE',
        'S','SSW','SW','WSW','W','WNW','NW','NNW'
    ]);
    if (validCompass.has(raw)) return raw;

    const degrees = finiteValue(raw);
    if (degrees !== null && degrees >= 0 && degrees <= 360) {
        return `${Math.round(degrees)}°`;
    }

    return '';
}

function metricAvailable(kind, value) {
    const n = finiteValue(value);
    if (n === null) return false;
    if (['temperatura','tmax','tmin','dewPoint','apparent','wetbulb','windchill'].includes(kind)) return n > -99.9;
    if (kind === 'humidex') return n > -30 && n < 80;
    if (kind === 'dewspread') return n >= 0 && n < 80;
    if (kind === 'umidita') return n >= 0 && n <= 100;
    if (kind === 'pressione') return n >= 850 && n <= 1100;
    if (['vento','raffica'].includes(kind)) return n >= 0 && n <= 250;
    if (['rain','rainrate','rainmonth','rainyear'].includes(kind)) return n >= 0 && n < 5000;
    if (kind === 'neve') return n >= 0 && n < 1000;
    if (kind === 'idro') return n > -50 && n < 100;
    if (kind === 'alt') return n >= 0 && n < 4000;
    return true;
}

function stationMetric(kind, label, value, unit, decimals = 1) {
    if (!metricAvailable(kind, value)) return null;
    return {
        kind,
        label,
        value: formatMetricValue(value, decimals),
        unit
    };
}

function renderMetricValue(metric, className) {
    const unit = metric?.unit ? `<small>${escapeHtml(metric.unit)}</small>` : '';
    return `<span class="${className}"><strong>${escapeHtml(metric?.value || '—')}</strong>${unit}</span>`;
}

function renderMetric(metric) {
    return `
      <div class="ws-station-card__row">
        <dt>${escapeHtml(metric.label)}</dt>
        <dd>${renderMetricValue(metric, 'ws-station-card__row-value')}</dd>
      </div>
    `;
}

function renderDetailSection(title, metrics, open = false) {
    const valid = metrics.filter(Boolean);
    if (!valid.length) return '';
    return `
      <details class="ws-station-card__details"${open ? ' open' : ''}>
        <summary>${escapeHtml(title)}</summary>
        <dl class="ws-station-card__list">
          ${valid.map(renderMetric).join('')}
        </dl>
      </details>
    `;
}

function renderFeaturedMetric(kind, label, value, unit, decimals = 1, modifier = '', extras = []) {
    const metric = stationMetric(kind, label, value, unit, decimals);
    const className = ['ws-station-card__featured-metric', modifier, metric ? '' : 'is-empty']
        .filter(Boolean)
        .join(' ');
    const extraItems = extras.filter(Boolean);
    return `
      <div class="${className}">
        <span class="ws-station-card__featured-label">${escapeHtml(label)}</span>
        ${renderMetricValue(metric, 'ws-station-card__featured-value')}
        ${extraItems.length ? `
        <div class="ws-station-card__featured-extra">
          ${extraItems.map(item => `
          <span class="${item.kind === 'tmax' ? 'is-max' : item.kind === 'tmin' ? 'is-min' : ''}">
            ${['tmax','tmin'].includes(item.kind) ? '<i aria-hidden="true"></i>' : ''}
            <b>${escapeHtml(item.label)}</b>
            ${renderMetricValue(item, 'ws-station-card__extra-value')}
          </span>
          `).join('')}
        </div>` : ''}
      </div>
    `;
}

function renderTemperatureFeatured(s) {
    const live = stationMetric('temperatura', 'Temperatura', s.temperatura, '°C', 1);
    const delta = key => `<span class="ws-temperature-delta" data-temperature-delta="${key}" title="Confronto in caricamento"><strong>—</strong><small>${key === 'live' ? 'vs ieri, stessa ora' : 'Ieri: —'}</small></span>`;
    const extremes = [['max','tmax','Massima',s.tmax],['min','tmin','Minima',s.tmin]];
    return `<div class="ws-station-card__featured-metric is-main is-temperature" data-temperature-trends>
        <span class="ws-station-card__featured-label">Temperatura</span>
        <div class="ws-temperature-live">${renderMetricValue(live,'ws-station-card__featured-value')}${delta('live')}</div>
        <div class="ws-temperature-extremes">${extremes.map(([key,kind,label,value]) => `<div class="ws-temperature-extreme is-${key}"><span class="ws-temperature-extreme__label">${key === 'max' ? '▲' : '▼'} ${key === 'max' ? 'Max' : 'Min'} oggi</span>${renderMetricValue(stationMetric(kind,label,value,'°C',1),'ws-temperature-extreme__value')}${delta(key)}</div>`).join('')}</div>
    </div>`;
}

// Card Pioggia dell'hero: giornaliera GRANDE + intensità (rain rate) accanto
// sulla stessa riga, annuale/mensile sotto come chip — stessa struttura (is-main)
// della card Temperatura, per uniformità grafica.
function renderRainFeatured(s) {
    const observation = s.rain_observation;
    const day = observation?.day;
    const interval = observation?.last_interval;
    const clock = value => {
        const date = new Date(value);
        return Number.isFinite(date.getTime()) ? date.toLocaleString('it-IT', {timeZone:'Europe/Rome',day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : '';
    };
    const daily = stationMetric('rain', 'Pioggia', s.pioggia, 'mm', 1);
    const measured = daily || (interval ? stationMetric('rain', 'Ultimo intervallo', interval.rain_mm, 'mm', 1) : null);
    const label = observation ? (daily && day ? 'Pioggia oggi' : interval ? `Pioggia · ${interval.hours} ore` : 'Pioggia') : 'Pioggia';
    const reference = daily && day ? `Dal ${clock(day.from)} al ${clock(day.to)}` : interval ? `${clock(interval.from)} – ${clock(interval.to)}` : '';
    const previousMonth = observation?.last_completed_month;
    const previousLabel = previousMonth ? new Date(`${previousMonth.from}T12:00:00`).toLocaleDateString('it-IT',{month:'long',year:'numeric'}) : '';
    const rate = stationMetric('rainrate', 'Intensità', s.rain_rate, 'mm/h', 1);
    const extras = [
        stationMetric('rainyear', 'Anno', s.pioggia_annuale, 'mm', 1),
        stationMetric('rainmonth', 'Mese', s.pioggia_mensile, 'mm', 1)
    ].filter(Boolean);
    const className = ['ws-station-card__featured-metric', 'is-main', 'is-rain', measured ? '' : 'is-empty']
        .filter(Boolean).join(' ');
    return `
      <div class="${className}">
        <span class="ws-station-card__featured-label">${escapeHtml(label)}</span>
        <div class="ws-station-card__featured-valrow">
          ${renderMetricValue(measured, 'ws-station-card__featured-value')}
          ${rate ? `<span class="ws-station-card__featured-sub"><b>Intensità</b>${renderMetricValue(rate, 'ws-station-card__sub-value')}</span>` : ''}
        </div>
        ${observation ? `<div style="font-size:12px;line-height:1.5;padding:8px 0;opacity:.85">${reference ? escapeHtml(reference) + '<br>' : ''}${(daily ? day?.trace_intervals > 0 : interval?.trace) ? 'Tracce di precipitazione<br>' : ''}${(observation.refresh_stale || observation.source_stale) ? 'Ultimo dato disponibile · ' : ''}Fonte: SYNOP${observation.month?.trace_intervals > 0 ? '<br>Mese corrente: tracce di precipitazione' : ''}${!daily && interval ? '<br>Intervallo osservato; totale di oggi non disponibile.' : ''}</div>` : ''}
        ${previousMonth ? `<div class="ws-station-card__featured-extra"><span><b>${escapeHtml(previousLabel)}</b>${renderMetricValue(stationMetric('rainmonth',previousLabel,previousMonth.rain_mm,'mm',1),'ws-station-card__extra-value')}</span></div>` : ''}
        ${extras.length ? `
        <div class="ws-station-card__featured-extra">
          ${extras.map(item => `
          <span><b>${escapeHtml(item.label)}</b>${renderMetricValue(item, 'ws-station-card__extra-value')}</span>
          `).join('')}
        </div>` : ''}
      </div>`;
}

function liveStationUrl(stationId) {
    const id = String(stationId || '').replace(/\D/g, '');
    return id ? `https://live.weathersicily.it/?station=${encodeURIComponent(id)}` : 'https://live.weathersicily.it';
}

// ======== Orario / timestamp (map-legend.js:1839, map-markers.js:1825) ========
const WS_HHMMSS_FMT = new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'Europe/Rome'
});

function formatTimeHHMMSS(timeLike) {
    if (!timeLike) return '';
    let d;
    if (typeof timeLike === 'number') {
        d = new Date(timeLike > 1e12 ? timeLike : timeLike * 1000);
    } else {
        d = new Date(timeLike);
    }
    if (isNaN(d)) return '';
    return WS_HHMMSS_FMT.format(d);
}

function stationTimestamp(station) {
    const raw = station?.time_utc ?? station?.last_update ?? station?.updated_at ?? station?.ora ?? station?.data ?? null;
    if (!raw) return null;
    const date = new Date(raw);
    return isNaN(date) ? null : date;
}

// ======== Contrasto hero (map-legend.js:1875) ========
function pickTextAndStroke(fillHex) {
    const lum = window.chroma(fillHex).luminance();
    const contrastBlack = (lum + 0.05) / 0.05;
    const contrastWhite = 1.05 / (lum + 0.05);
    const fill = contrastBlack >= contrastWhite ? '#050505' : '#ffffff';
    const stroke = fill === '#050505' ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.32)';
    return { fill, stroke };
}

// ======== Webcam (map-markers.js:493-533) ========
// Criterio di associazione v1: le webcam arrivano GIÀ agganciate al record
// stazione (campo s.webcams, slug dall'API /stations/live) — nessuna chiamata
// a WSWeatherApi.getWebcams() né filtro geografico.
function normalizeStationWebcams(s) {
    const rows = Array.isArray(s?.webcams) ? s.webcams : [];
    return rows.map((item, index) => {
        // L'API live ritorna ogni webcam come slug ("capodorlando1"), payload
        // più vecchi/altre sorgenti come oggetti. Supporta entrambi.
        const obj = (typeof item === 'string') ? { slug: item } : (item || {});
        const slug = String(obj.slug || obj.container_name || '').trim();

        let url = sanitizeHttpUrl(obj.url || obj.link || obj.page_url || obj.href || '');
        let image = sanitizeHttpUrl(obj.image_url || obj.image || obj.thumbnail || obj.thumb || '');

        if (slug) {
            const encoded = encodeURIComponent(slug);
            if (!url) url = `${WEBCAM_BASE_URL}/cam/${encoded}`;
            if (!image) image = `${WEBCAM_BASE_URL}/api/snapshot/${encoded}/latest.jpg`;
        }

        const title = obj.location || obj.name || obj.title || (slug ? `Webcam ${slug}` : `Webcam ${index + 1}`);
        return { url, image, title };
    }).filter(item => item.url || item.image).slice(0, 3);
}

function renderStationWebcamsHtml(s) {
    const webcams = normalizeStationWebcams(s);
    if (!webcams.length) return '';
    return `
      <section class="ws-station-card__section">
        <h4>Webcam</h4>
        <div class="ws-station-card__webcams">
          ${webcams.map(webcam => `
            <a class="ws-station-card__webcam" href="${escapeHtml(webcam.url || webcam.image)}" target="_blank" rel="noopener">
              ${webcam.image ? `<img src="${escapeHtml(webcam.image)}" alt="${escapeHtml(webcam.title)}" loading="lazy">` : '<span>Live</span>'}
              <strong>${escapeHtml(webcam.title)}</strong>
            </a>
          `).join('')}
        </div>
      </section>
    `;
}

// ======== Archivio 7 giorni (map-markers.js:551-625) ========
function archiveFieldForParam(parametro) {
    switch (parametro) {
        case 'umidita': return ['humidity', 'umidita'];
        case 'vento': return ['wind', 'vento'];
        case 'raffica': return ['wind_gust', 'rafficaMax', 'raffica'];
        case 'pressione': return ['pressure', 'pressione'];
        case 'temp_mare': return ['sea_temperature', 'temp_mare', 'water_temperature', 'waterTemperature', 'temperatura_mare'];
        case 'livello_mare': return ['sea_level', 'livello_mare', 'idrometrico', 'hydrometric_level', 'livello_idrometrico'];
        case 'rain':
        case 'rainrate': return ['rain_day', 'dayRain', 'pioggia'];
        case 'dewPoint': return ['dew_point', 'dewPoint'];
        case 'tmax':
        case 'tmin':
        case 'temperatura':
        default: return ['temperature', 'temperatura'];
    }
}

function firstFiniteFrom(row, keys) {
    for (const key of keys) {
        const n = finiteValue(row?.[key]);
        if (n !== null) return n;
    }
    return null;
}

const stationArchiveCache = new Map();

function getArchivePointsForStation(id) {
    const key = `${id}:last7d`;
    if (!stationArchiveCache.has(key)) {
        stationArchiveCache.set(key, window.WSWeatherApi.getStationArchive(id, { range: 'last7d' }));
    }
    return stationArchiveCache.get(key);
}

function archivePointDate(point) {
    const date = new Date(point?.observed_at || point?.data_rilevazione || point?.timestamp || '');
    return isNaN(date) ? null : date;
}

function archiveRainValue(point) {
    return firstFiniteFrom(point, ['rain_day', 'dayRain', 'pioggia']);
}

function rollingRainTotal(rows, latestDate, hours) {
    const since = new Date(latestDate.getTime() - (hours * 60 * 60 * 1000));
    const ordered = rows
        .map(point => ({ date: archivePointDate(point), value: archiveRainValue(point) }))
        .filter(point => point.date && point.value !== null && point.date <= latestDate)
        .sort((a, b) => a.date - b.date);
    if (ordered.length < 2) return null;

    const previous = [...ordered].reverse().find(point => point.date < since) || null;
    const segment = ordered.filter(point => point.date >= since);
    const sequence = previous ? [previous, ...segment] : segment;
    if (sequence.length < 2) return null;

    let total = 0;
    for (let i = 1; i < sequence.length; i++) {
        const before = sequence[i - 1].value;
        const current = sequence[i].value;
        const delta = current - before;
        total += delta >= 0 ? delta : Math.max(0, current);
    }
    return Math.max(0, total);
}

// ======== Pioggia ultime ore (map-markers.js:627-675) ========
async function renderStationRainWindows(container, station) {
    const id = String(station?.id || '').trim();
    const holder = container.querySelector('[data-station-rain-windows]');
    if (!id || !holder || !window.WSWeatherApi?.getStationArchive) return;

    try {
        const points = await getArchivePointsForStation(id);
        const rows = Array.isArray(points) ? points : [];
        const latest = rows
            .map(archivePointDate)
            .filter(Boolean)
            .sort((a, b) => b - a)[0] || null;
        const stationDate = stationTimestamp(station);

        if (!latest || (stationDate && Math.abs(stationDate - latest) > 3 * 60 * 60 * 1000)) {
            holder.innerHTML = '<p class="ws-station-card__chart-note">Accumuli mobili non disponibili per questa stazione.</p>';
            return;
        }

        const windows = [
            { label: '1h', hours: 1 },
            { label: '3h', hours: 3 },
            { label: '6h', hours: 6 },
            { label: '12h', hours: 12 },
            { label: '24h', hours: 24 }
        ].map(item => ({
            ...item,
            value: rollingRainTotal(rows, latest, item.hours)
        }));

        if (!windows.some(item => item.value !== null)) {
            holder.innerHTML = '<p class="ws-station-card__chart-note">Accumuli mobili non disponibili per questa stazione.</p>';
            return;
        }

        holder.innerHTML = `
            <dl class="ws-rain-window-grid">
                ${windows.map(item => `
                    <div>
                        <dt>${escapeHtml(item.label)}</dt>
                        <dd>${item.value === null ? 'n.d.' : escapeHtml(item.value.toFixed(item.value < 10 ? 1 : 0))}<span>mm</span></dd>
                    </div>
                `).join('')}
            </dl>
        `;
    } catch (error) {
        holder.innerHTML = '<p class="ws-station-card__chart-note">Accumuli mobili temporaneamente non disponibili.</p>';
    }
}

// ======== Chart.js on-demand (pattern wsEnsureChart, index.php v1:786) ========
let chartJsPromise = null;

function ensureChartJs() {
    if (typeof window.wsEnsureChart === 'function') return window.wsEnsureChart();
    if (window.Chart) return Promise.resolve();
    if (chartJsPromise) return chartJsPromise;
    chartJsPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = CHART_VENDOR_URL;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => { chartJsPromise = null; reject(new Error('chart load failed')); };
        document.head.appendChild(s);
    });
    return chartJsPromise;
}

// ======== Grafico 7 giorni (map-markers.js:685-793) ========
function destroyChartIn(holder) {
    const canvas = holder?.querySelector('canvas');
    if (canvas?._wsStationChart) {
        canvas._wsStationChart.destroy();
        canvas._wsStationChart = null;
    }
}

function setStationChartActive(container, parametro) {
    container.querySelectorAll('[data-station-chart-param]').forEach(button => {
        button.classList.toggle('is-active', button.getAttribute('data-station-chart-param') === parametro);
    });
}

function chartThemeColors() {
    // Adattamento v2: i colori di tick/griglia seguono i token del tema
    // (fallback = costanti v1). Solo resa, nessun impatto sui valori.
    let tick = '';
    let grid = '';
    try {
        const css = getComputedStyle(document.documentElement);
        tick = (css.getPropertyValue('--ws-fg-soft') || '').trim();
        grid = (css.getPropertyValue('--ws-line') || '').trim();
    } catch (e) { /* fallback sotto */ }
    return {
        tick: tick || '#627383',
        grid: grid || 'rgba(16,32,45,0.07)'
    };
}

async function renderStationArchiveChart(container, station, parametro) {
    const id = String(station?.id || '').trim();
    if (!id || !window.WSWeatherApi?.getStationArchive) return;
    // Chart.js caricato on-demand (non al boot): lo serve solo questa scheda.
    try { await ensureChartJs(); } catch (e) { return; }
    if (typeof window.Chart !== 'function') return;
    const holder = container.querySelector('[data-station-chart]');
    const canvas = holder?.querySelector('canvas');
    if (!canvas || !holder) return;
    setStationChartActive(container, parametro);
    holder.querySelector('.ws-station-card__chart-note')?.remove();
    canvas.hidden = false;

    try {
        const points = await getArchivePointsForStation(id);
        const keys = archiveFieldForParam(parametro);
        const rows = (Array.isArray(points) ? points : [])
            .map(point => ({
                date: new Date(point.observed_at || point.data_rilevazione || point.timestamp || ''),
                value: firstFiniteFrom(point, keys)
            }))
            .filter(point => !isNaN(point.date) && point.value !== null)
            .sort((a, b) => a.date - b.date);

        if (!rows.length) {
            destroyChartIn(holder);
            holder.innerHTML = `
                <canvas aria-label="Grafico storico stazione" hidden></canvas>
                <p class="ws-station-card__chart-note">Storico non disponibile per questo parametro.</p>
            `;
            return;
        }

        const sampled = rows.length > 56
            ? rows.filter((_, index) => index % Math.ceil(rows.length / 56) === 0)
            : rows;

        destroyChartIn(holder);

        const unit = chartUnitForParam(parametro);
        const scaleFn = getColorScale(parametro);
        const latestColor = (() => {
            const last = sampled[sampled.length - 1]?.value;
            try {
                // Le scale a intervalli ritornano direttamente una stringa
                // colore, quelle chroma un oggetto (→ .hex()); v1 faceva lo
                // stesso distinguendo per identità di funzione.
                const c = scaleFn(last);
                return window.chroma(typeof c === 'string' ? c : c.hex()).hex();
            } catch (e) {
                return '#0079b8';
            }
        })();
        const theme = chartThemeColors();

        const chart = new window.Chart(canvas, {
            type: ['rain','rainrate'].includes(parametro) ? 'bar' : 'line',
            data: {
                labels: sampled.map(row => new Intl.DateTimeFormat('it-IT', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    timeZone: 'Europe/Rome'
                }).format(row.date)),
                datasets: [{
                    label: `${parametro}${unit ? ' (' + unit + ')' : ''}`,
                    data: sampled.map(row => row.value),
                    borderColor: latestColor,
                    backgroundColor: window.chroma(latestColor).alpha(0.22).css(),
                    borderWidth: 2,
                    pointRadius: sampled.length > 34 ? 0 : 2,
                    tension: 0.28,
                    fill: !['rain','rainrate'].includes(parametro)
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: { intersect: false, mode: 'index' }
                },
                scales: {
                    x: {
                        ticks: { maxTicksLimit: 5, color: theme.tick, font: { size: 10, weight: '700' } },
                        grid: { display: false }
                    },
                    y: {
                        ticks: { color: theme.tick, font: { size: 10, weight: '700' } },
                        grid: { color: theme.grid }
                    }
                }
            }
        });
        canvas._wsStationChart = chart;
    } catch (error) {
        destroyChartIn(holder);
        holder.innerHTML = `
            <canvas aria-label="Grafico storico stazione" hidden></canvas>
            <p class="ws-station-card__chart-note">Grafico temporaneamente non disponibile.</p>
        `;
    }
}

// ======== Wiring interno grafico (map-markers.js:795-814) ========
function bindStationChartControls(container, station, parametro) {
    const chartDetails = container.querySelector('[data-station-chart-details]');
    if (chartDetails) {
        chartDetails.addEventListener('toggle', () => {
            if (!chartDetails.open) return;
            const active = chartDetails.querySelector('[data-station-chart-param].is-active');
            const nextParam = active?.getAttribute('data-station-chart-param') || parametro;
            renderStationArchiveChart(container, station, nextParam);
        }, { once: true });
    }
    container.querySelectorAll('[data-station-chart-param]').forEach(button => {
        button.addEventListener('click', () => {
            const nextParam = button.getAttribute('data-station-chart-param') || parametro;
            renderStationArchiveChart(container, station, nextParam);
        });
    });
}

// ======== API pubblica ========
function stationOf(rec) {
    // Accetta sia il display-record ({station, lat, lon, ...} da
    // stationDisplayRecord) sia direttamente il record stazione grezzo.
    if (rec && typeof rec === 'object' && rec.station && typeof rec.station === 'object') return rec.station;
    return rec || null;
}

// Port di getPopupText (map-markers.js:898-1026). `parametro` sostituisce la
// lettura di #selectParametro; il resto è identico (stessi guard, decimali,
// etichette, URL).
export function buildStationCardHtml(rec, parametro = 'temperatura', map = null) {
    const s = stationOf(rec) || {};
    const name = s.localita || s.location || s.comune || s.city || s.name || s.nome || 'Stazione';
    const networkFull = stationNetworkFullLabel(s);
    const hardwareLabel = String(s.hardware_label || '').trim();
    const networkBadge = stationNetworkBadgeLabel(s);
    const networkLogo = stationNetworkLogo(s, networkFull);
    const networkSource = networkFull !== networkBadge ? networkFull : '';
    const ts = s.time_utc ?? s.last_update ?? s.updated_at ?? s.ora ?? s.data ?? null;
    const time = formatTimeHHMMSS(ts);
    const url = sanitizeHttpUrl(s.pagina);
    const rainwatchSource = ['rainwatch', 'unipa'].includes(String(s.network_category || '')) || Number(s.officialFlag ?? s.official) === 13;
    const isUnipa = String(s.network_category || '') === 'unipa';
    const liveUrl = isUnipa ? (url || 'https://idrologia.unipa.it/it/data') : liveStationUrl(s.id);
    const stationUrl = new URL(window.location.pathname, window.location.origin);
    stationUrl.searchParams.set('station', s.id || '');
    stationUrl.searchParams.set('parametro', parametro);
    if (map) appendCameraToUrl(stationUrl, map);
    const shareUrl = stationUrl.toString();
    const alt = finiteValue(s.alt);
    const direction = cleanWindDirection(s.direzione);
    const altitudeLabel = metricAvailable('alt', alt) ? `${formatMetricValue(alt, 0)} m s.l.m.` : '';
    const temperatureRaw = finiteValue(s.temperatura);

    let heroColor = '#29b8ec';
    let heroDark = '#0079b8';
    let heroContrast = { fill: '#ffffff', stroke: 'rgba(0,0,0,0.32)' };
    try {
        const scaleForHero = getColorScale('temperatura');
        const heroRawColor = metricAvailable('temperatura', temperatureRaw)
            ? scaleForHero(temperatureRaw).hex()
            : '#29b8ec';
        heroColor = window.chroma(heroRawColor).hex();
        heroDark = window.chroma(heroColor).darken(0.85).hex();
        heroContrast = pickTextAndStroke(heroColor);
    } catch (e) { /* fallback ai default sopra */ }

    const dewPointExtras = [
        stationMetric('umidita', 'UR', s.umidita, '%', 0)
    ];
    const windExtras = [
        direction ? { label: 'Dir', value: direction, unit: '' } : null,
        stationMetric('raffica', 'Raffica', s.raffica, 'km/h', 0)
    ];

    const todayParams = new Set(['temperatura', 'tmax', 'tmin', 'dewPoint', 'umidita', 'pressione', 'apparent', 'humidex', 'wetbulb', 'dewspread', 'windchill']);
    const otherParams = new Set(['neve', 'idro', 'livello_mare', 'temp_mare']);
    const rainParams = new Set(['rain', 'rainrate', 'rainmonth', 'rainyear']);
    const todayOpen = todayParams.has(parametro);
    const otherOpen = otherParams.has(parametro);
    const rainOpen = rainParams.has(parametro);
    const chartOpen = !todayOpen && !otherOpen && !rainOpen && STATION_CHART_PARAMS.some(item => item.id === parametro);

    // I valori già presenti nei quattro riquadri principali non vengono
    // ripetuti qui sotto: i pannelli raccolgono solo informazioni aggiuntive.
    const sections = [
        renderDetailSection('Dati attuali e indici', [
            stationMetric('pressione', 'Pressione', s.pressione, 'hPa', 0),
            stationMetric('apparent', 'Percepita', computeDerivedValue(s, 'apparent'), '°C', 1),
            stationMetric('humidex', 'Humidex', computeDerivedValue(s, 'humidex'), '', 0),
            stationMetric('wetbulb', 'Bulbo umido', computeDerivedValue(s, 'wetbulb'), '°C', 1),
            stationMetric('dewspread', 'Spread T-Dp', computeDerivedValue(s, 'dewspread'), '°C', 1),
            stationMetric('windchill', 'Wind Chill', computeDerivedValue(s, 'windchill'), '°C', 1)
        ], todayOpen),
        renderDetailSection('Altri parametri', [
            stationMetric('neve', 'Neve', snowIsPlausible(s) ? s.livello_neve : null, 'cm', 0),
            stationMetric('idro', 'Idrometro', s.livello_idrometrico, 'm', 2)
        ], otherOpen)
    ].join('');
    return `
    <article class="ws-station-card">
      <header class="ws-station-card__head">
        <div>
          <span class="ws-station-card__badge">${escapeHtml(networkBadge)}</span>
          ${(s.weather_sicily_instant === true || s.data_mode === 'instant') ? `<span class="ws-station-card__instant"><i>ϟ</i> INSTANT · ${Number(s.refresh_seconds || 2.5).toLocaleString('it-IT')} s</span>` : ''}
          <div class="ws-station-card__title">
            <h3>${escapeHtml(name)}</h3>
            ${networkSource ? `<span class="ws-station-card__source">${rainwatchSource && url ? `<a href="${escapeHtml(url)}" target="_blank" rel="noopener">Fonte: ${escapeHtml(networkSource)}</a>` : escapeHtml(networkSource)}${String(s.network_category || '') === 'unipa' ? ' · dati non validati dalla fonte' : ''}</span>` : ''}
            ${hardwareLabel ? `<span class="ws-station-card__source ws-station-card__hardware" title="Tipo di stazione"><span aria-hidden="true">◈</span> ${escapeHtml(hardwareLabel)}${s.hardware_status === 'brand_only' ? ' · modello da confermare' : ''}</span>` : ''}
            ${altitudeLabel ? `<span class="ws-station-card__altitude"><span aria-hidden="true">⛰️</span> ${escapeHtml(altitudeLabel)}</span>` : ''}
          </div>
        </div>
        ${(networkLogo || time) ? `
        <div class="ws-station-card__head-meta">
          ${networkLogo ? `${networkLogo.href ? `<a class="ws-station-card__network-logo-link" href="${escapeHtml(networkLogo.href)}" target="_blank" rel="noopener" aria-label="Apri ${escapeHtml(networkLogo.alt)}">` : ''}<img class="ws-station-card__network-logo ws-station-card__network-logo--${escapeHtml(networkLogo.modifier)}" src="${escapeHtml(networkLogo.src)}" alt="${escapeHtml(networkLogo.alt)}" loading="lazy" decoding="async">${networkLogo.href ? '</a>' : ''}` : ''}
          ${time ? `<span class="ws-station-card__time">${escapeHtml(time)}</span>` : ''}
        </div>` : ''}
      </header>
      <div class="ws-station-card__hero" style="--station-hero:${escapeHtml(heroColor)};--station-hero-dark:${escapeHtml(heroDark)};--station-hero-text:${escapeHtml(heroContrast.fill)};">
        <div class="ws-station-card__featured-grid">
          ${renderTemperatureFeatured(s)}
          ${renderFeaturedMetric('vento', 'Vento', s.vento, 'km/h', 0, 'is-wind', windExtras)}
          ${renderRainFeatured(s)}
          ${renderFeaturedMetric('dewPoint', 'Dew point', s.dewPoint, '°C', 1, 'is-dew', dewPointExtras)}
        </div>
      </div>
      ${window.WSComfort ? WSComfort.html(s.comfort,true) : ""}
      <div data-station-thermal></div>
      ${sections}
      ${s.id ? `<details class="ws-station-card__details" data-station-degree-days>
        <summary>Gradi giorno · <span data-degree-day-date>dati da verificare</span></summary>
        <dl class="ws-station-card__list">
          <div class="ws-station-card__row"><dt>Heat deg</dt><dd class="ws-station-card__row-value" data-heat-deg>—</dd></div>
          <div class="ws-station-card__row"><dt>Cool deg</dt><dd class="ws-station-card__row-value" data-cool-deg>—</dd></div>
        </dl>
        <p class="ws-station-card__chart-note">Base 18,3 °C. Media del giorno = (massima + minima) / 2. Heat deg indica quanto la media è sotto la base; Cool deg quanto è sopra. Il valore è riferito a un giorno completo.</p>
      </details>` : ''}
      <div data-station-webcams></div>
      ${s.id ? `
      <details class="ws-station-card__details" data-station-chart-details${chartOpen ? ' open' : ''}>
        <summary>Andamento 7 giorni</summary>
        <div class="ws-station-card__chart-tools" aria-label="Parametro grafico">
          ${STATION_CHART_PARAMS.map(item => `
            <button type="button" data-station-id="${escapeHtml(String(s.id))}" data-station-chart-param="${escapeHtml(item.id)}">${escapeHtml(item.label)}</button>
          `).join('')}
        </div>
        <div class="ws-station-card__mini-chart" data-station-chart="${escapeHtml(String(s.id))}">
          <canvas aria-label="Grafico storico stazione"></canvas>
        </div>
      </details>` : ''}
      ${s.id ? `
      <details class="ws-station-card__details"${rainOpen ? ' open' : ''}>
        <summary>Pioggia ultime ore</summary>
        <div class="ws-station-card__rain-windows" data-station-rain-windows="${escapeHtml(String(s.id))}">
          <p class="ws-station-card__chart-note">Calcolo accumuli...</p>
        </div>
      </details>` : ''}
      <div class="ws-station-card__actions">
        <a class="ws-station-card__link" data-action="open-live" href="${escapeHtml(liveUrl)}" target="_blank" rel="noopener">${isUnipa ? 'Apri dati HYCLIC ↗' : 'Apri su Weather Sicily Live ↗'}</a>
        ${url ? `<a class="ws-station-card__link ws-station-card__link--secondary" data-action="open-page" href="${escapeHtml(url)}" target="_blank" rel="noopener">Pagina stazione ↗</a>` : ''}
        ${s.id ? `<a class="ws-station-card__link ws-station-card__link--ghost" data-action="share" href="${escapeHtml(shareUrl)}">Condividi stazione ↗</a>` : ''}
        ${s.id ? `<button type="button" class="ws-station-card__link ws-station-card__link--ghost ws-station-card__button" data-action="report-station" data-report-station data-station-id="${escapeHtml(String(s.id))}" data-station-name="${escapeHtml(name)}">Segnala anomalia</button>` : ''}
      </div>
      ${renderStationAd()}
    </article>
  `;
}

// Idratazione della scheda già inserita nel DOM: webcam (sincrone, dal record
// stazione come in v1), wiring grafico 7 giorni (lazy all'apertura del
// <details>, come v1) e finestre pioggia mobili (async, subito come v1).
export async function hydrateStationCard(container, rec, parametro = 'temperatura') {
    const s = stationOf(rec);
    if (!container || !s) return;

    const camHolder = container.querySelector('[data-station-webcams]');
    if (camHolder) camHolder.innerHTML = renderStationWebcamsHtml(s);

    initStationAd(container);
    void hydrateThermalContext(container, rec);
    void hydrateTemperatureTrends(container, rec);

    const degreeDays = container.querySelector('[data-station-degree-days]');
    if (degreeDays && /^\d+$/.test(String(s.id))) {
        void fetch(`https://history.weathersicily.it/api/degree-days.php?station=${encodeURIComponent(s.id)}&v=1`)
            .then(response => response.ok ? response.json() : null)
            .then(data => {
                const row = data?.degree_days;
                if (!degreeDays.isConnected) return;
                if (!row) { degreeDays.querySelector('[data-degree-day-date]').textContent = 'dati insufficienti'; return; }
                degreeDays.querySelector('[data-degree-day-date]').textContent = new Date(`${row.day}T12:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
                degreeDays.querySelector('[data-heat-deg]').textContent = `${Number(row.heating).toLocaleString('it-IT', {minimumFractionDigits: 1})} °C·giorno`;
                degreeDays.querySelector('[data-cool-deg]').textContent = `${Number(row.cooling).toLocaleString('it-IT', {minimumFractionDigits: 1})} °C·giorno`;
            }).catch(() => {});
    }

    const initialChartParam = STATION_CHART_PARAMS.some(item => item.id === parametro) ? parametro : 'temperatura';
    setStationChartActive(container, initialChartParam);
    bindStationChartControls(container, s, parametro);
    const chartDetails = container.querySelector('[data-station-chart-details]');
    if (chartDetails?.open) renderStationArchiveChart(container, s, initialChartParam);

    await renderStationRainWindows(container, s);
}
